import { useEffect } from "react";
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import type { Game } from "@/lib/types";
import { GAME_LABEL } from "@/lib/types";
import { useProfileStore } from "@/state/profile";
import { useSessionStore } from "@/state/session";
import { navigate } from "@/lib/router";
import { GameResults } from "./GameResults";
import { cn } from "@/lib/cn";

interface Props {
  game: Game;
  children: (props: {
    item: NonNullable<ReturnType<typeof useSessionStore.getState>["loaded"]>["session"]["items"][number];
    disabled: boolean;
  }) => ReactNode;
}

export function GameShell({ game, children }: Props) {
  const profileId = useProfileStore((s) => s.activeId);
  const status = useSessionStore((s) => s.status);
  const error = useSessionStore((s) => s.error);
  const loaded = useSessionStore((s) => s.loaded);
  const load = useSessionStore((s) => s.load);
  const reset = useSessionStore((s) => s.reset);
  const complete = useSessionStore((s) => s.complete);

  useEffect(() => {
    load(profileId, game);
    return () => reset();
  }, [profileId, game, load, reset]);

  useEffect(() => {
    if (loaded?.done && !loaded.session.completed_at) {
      complete(profileId);
    }
  }, [loaded?.done, loaded?.session.completed_at, complete, profileId]);

  if (status === "loading" || status === "idle") {
    return <LoadingScreen game={game} />;
  }
  if (status === "error" || !loaded) {
    return <ErrorScreen message={error ?? "no data"} />;
  }

  const label = GAME_LABEL[game];

  if (loaded.done) {
    return <GameResults game={game} />;
  }

  const item = loaded.session.items[loaded.idx];
  if (!item) return <GameResults game={game} />;
  const disabled = !!loaded.lastResult;
  const progress = (loaded.idx / loaded.session.items.length) * 100;

  return (
    <div className="min-h-[100dvh] flex flex-col">
      <header className="safe-pt px-4 pb-3">
        <div className="w-full max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <button
              aria-label="Back"
              onClick={() => navigate({ name: "home" })}
              className="h-10 w-10 rounded-2xl grid place-items-center bg-brand-surface border border-brand-border shadow-chunk"
            >
              ←
            </button>
            <div className="flex-1 flex items-center gap-2">
              <span className="text-2xl">{label.emoji}</span>
              <div>
                <div className="font-display text-lg leading-none">{label.title}</div>
                <div className="text-xs text-brand-muted uppercase tracking-widest font-extrabold">
                  {loaded.idx + 1} / {loaded.session.items.length}
                </div>
              </div>
            </div>
            <ComboBadge combo={loaded.combo} />
          </div>
          <div className="mt-3 h-2 rounded-full bg-brand-border overflow-hidden">
            <motion.div
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              className="h-full bg-brand-coral"
            />
          </div>
          {item.srs && (
            <div className="mt-2 flex justify-center">
              <span className="pill bg-brand-saffron/20 border-brand-saffron/40 text-brand-ink">
                Repaso · SRS
              </span>
            </div>
          )}
          <div className="mt-2 flex justify-center">
            <span className="text-[10px] uppercase tracking-widest text-brand-muted font-black">
              {loaded.session.support_level === "guided"
                ? "Ayuda guiada"
                : loaded.session.support_level === "challenge"
                  ? "Modo reto"
                  : "Nivel equilibrado"}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-lg mx-auto px-4 pb-28">
        {children({ item, disabled })}
      </main>
    </div>
  );
}

function ComboBadge({ combo }: { combo: number }) {
  if (combo < 2) return <span className="w-12" aria-hidden />;
  return (
    <motion.div
      key={combo}
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={cn(
        "chunk px-3 py-1 flex items-center gap-1 text-sm font-black",
        combo >= 10
          ? "bg-gradient-to-r from-brand-saffron to-brand-coral text-white animate-shine bg-[length:200%_100%]"
          : "",
      )}
    >
      <span className="text-base">🔥</span>
      <span>{combo}</span>
    </motion.div>
  );
}

function LoadingScreen({ game }: { game: Game }) {
  const label = GAME_LABEL[game];
  return (
    <div className="min-h-[100dvh] grid place-items-center text-center p-6">
      <div>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "linear" }}
          className="text-5xl mx-auto w-fit"
        >
          {label.emoji}
        </motion.div>
        <div className="mt-4 font-display text-xl">Preparando {label.title}…</div>
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="min-h-[100dvh] grid place-items-center text-center p-6">
      <div className="chunk p-5 max-w-sm">
        <div className="text-4xl">🙈</div>
        <div className="mt-2 font-display text-xl">Algo falló</div>
        <p className="mt-1 text-sm text-brand-muted font-bold">{message}</p>
        <button
          onClick={() => navigate({ name: "home" })}
          className="mt-4 chunk-btn-primary w-full"
        >
          Volver
        </button>
      </div>
    </div>
  );
}
